import Button from "@/Components/Button/Button";
import PosTerminalSettingModal from "@/Components/Modal/PosTerminalSettingModal";
import PrinterSettingModal from "@/Components/Modal/PrinterSettingModal";
import { Numpad } from "@/Components/NumbersKeyPad/NumbersKeyPad";
import useFullScreen from "@/shared/atoms/useFullScreen";
import useAuthHook from "@/shared/hooks/useAuthHook";
import useKioskPinCodeVerify from "@/shared/mutations/kioskSettings/useKioskPinCodeVerify";
import usePosUserSettingsQuery from "@/shared/queries/pos/usePosUserSettingsQuery";
import { setKioskModeSafe } from "@/shared/utils/electronKioskMode";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type SecretView = "closed" | "pin" | "menu" | "printer" | "terminal";

export default function KioskExit() {
  const { t } = useTranslation();
  const [_, setFullScreen] = useFullScreen();
  const { signOut } = useAuthHook();
  const verifyPinCode = useKioskPinCodeVerify();

  const [tapCount, setTapCount] = useState(0);
  const [view, setView] = useState<SecretView>("closed");
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(false);
  const [pinPadKey, setPinPadKey] = useState(0);
  const timeoutRef = useRef<number | null>(null);
  // Where to go when the terminal modal closes: back to the menu when opened
  // from there, otherwise fully closed (auto-open when no terminal is set).
  const [terminalReturnView, setTerminalReturnView] =
    useState<SecretView>("closed");
  const hasAutoOpenedTerminalRef = useRef(false);
  const { data: posUserSettings, isSuccess: isPosUserSettingsLoaded } =
    usePosUserSettingsQuery();

  useEffect(() => {
    if (!isPosUserSettingsLoaded || hasAutoOpenedTerminalRef.current) return;
    hasAutoOpenedTerminalRef.current = true;

    if (!posUserSettings?.default_pos_terminal_id) {
      setTerminalReturnView("closed");
      setView("terminal");
    }
  }, [isPosUserSettingsLoaded, posUserSettings?.default_pos_terminal_id]);

  const closeSecret = () => {
    setView("closed");
    setPin("");
    setPinError(false);
    setTapCount(0);
  };

  const handleSecretTap = () => {
    const next = tapCount + 1;
    setTapCount(next);

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      setTapCount(0);
    }, 1500);

    if (next >= 5) {
      setTapCount(0);
      setPin("");
      setPinError(false);
      setPinPadKey((key) => key + 1);
      setView("pin");
    }
  };

  const submitPin = async () => {
    const isValid = await verifyPinCode.mutateAsync(pin).catch(() => false);
    if (isValid) {
      setPin("");
      setPinError(false);
      setView("menu");
      return;
    }

    setPinError(true);
    setPin("");
    setPinPadKey((key) => key + 1);
  };

  const handleRestart = () => {
    window.location.replace("/kiosk");
  };

  const handleExitKiosk = async () => {
    await setKioskModeSafe(false);
    setFullScreen(false);
    closeSecret();
  };

  const handleLogout = () => {
    signOut();
    closeSecret();
  };

  return (
    <>
      <div
        onClick={handleSecretTap}
        className="fixed right-0 top-0 z-[9999] h-[70px] w-[70px] bg-transparent"
      />

      {view === "pin" && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-[rgba(0,0,0,0.45)]">
          <div className="flex w-[36rem] flex-col items-center justify-center rounded-2xl bg-white p-8">
            <h2 className="mb-2 text-center text-[2.2rem] font-semibold">
              {t("kioskPage.secretMenuTitle")}
            </h2>
            <p className="mb-6 text-center text-small opacity-70">
              {t("kioskPage.secretMenuPinHint")}
            </p>
            <input
              value={pin}
              onChange={(e) => {
                setPin(e.target.value);
                setPinError(false);
              }}
              placeholder={t("kioskPage.enterPin")}
              autoFocus
              type="password"
              inputMode="numeric"
              className={`mb-4 box-border w-full rounded-xl border p-[1.4rem] text-[2.2rem] tracking-[0.4rem] ${
                pinError ? "border-error" : "border-border"
              }`}
            />
            {pinError && (
              <div
                role="alert"
                className="mb-4 box-border w-full rounded-lg bg-[#fdecec] px-4 py-3 text-center text-small font-medium text-error"
              >
                {t("pincodeInCorrect")}
              </div>
            )}
            <Numpad
              key={pinPadKey}
              onChange={(v) => {
                setPin(v);
                setPinError(false);
              }}
            />
            <div className="mt-8 flex w-full justify-end gap-3">
              <Button type="button" variant="secondary" onClick={closeSecret}>
                {t("cancel")}
              </Button>
              <Button
                type="button"
                onClick={submitPin}
                isPending={verifyPinCode.isPending}
              >
                {t("kioskPage.openSecretMenu")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {view === "menu" && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-[rgba(0,0,0,0.45)]">
          <div className="flex w-[40rem] flex-col rounded-2xl bg-white p-8">
            <h2 className="mb-2 text-[2.2rem] font-semibold">
              {t("kioskPage.secretMenuTitle")}
            </h2>
            <p className="mb-8 text-small opacity-70">
              {t("kioskPage.secretMenuDescription")}
            </p>

            <div className="flex flex-col gap-4">
              <Button type="button" size="large" onClick={handleRestart}>
                {t("kioskPage.restartWindow")}
              </Button>
              <Button
                type="button"
                size="large"
                variant="secondary"
                onClick={() => setView("printer")}
              >
                {t("kioskPage.printerSettings")}
              </Button>
              <Button
                type="button"
                size="large"
                variant="secondary"
                onClick={() => {
                  setTerminalReturnView("menu");
                  setView("terminal");
                }}
              >
                {t("kioskPage.managePosTerminal")}
              </Button>
              <Button
                type="button"
                size="large"
                variant="secondary"
                onClick={handleLogout}
              >
                {t("kioskPage.logout")}
              </Button>
              <Button
                type="button"
                size="large"
                variant="danger"
                onClick={handleExitKiosk}
              >
                {t("kioskPage.exitKiosk")}
              </Button>
            </div>

            <div className="mt-8 flex justify-end">
              <Button type="button" variant="secondary" onClick={closeSecret}>
                {t("cancel")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {view === "printer" && (
        <PrinterSettingModal show onExit={() => setView("menu")} />
      )}

      {view === "terminal" && (
        <PosTerminalSettingModal
          show
          onExit={() => setView(terminalReturnView)}
        />
      )}
    </>
  );
}
